import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { AuthContextType, AuthUser } from '../types/Auth';
import AuthService from '../services/AuthService';
import { setSessionLapsedFlag } from '../services/sessionLapse';
import { clearPortalAdminTenantOverride } from '../utils/portalAdminTenantOverride';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Don't let a failing provider turn the 60s tick into a renewal loop. A
// deliberate click (`renewSessionNow`) is exempt: the cooldown exists to
// protect the *automatic* attempt from itself, and a user who presses the
// button after a failure is entitled to an immediate retry.
const RENEW_COOLDOWN_MS = 5 * 60 * 1000;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<Date | null>(null);
  const [expiryWarningDismissed, setExpiryWarningDismissed] = useState(false);
  const [sessionLapsed, setSessionLapsed] = useState(false);
  const [reauthRequested, setReauthRequested] = useState(false);
  const [renewalRefusal, setRenewalRefusal] = useState<string | null>(null);

  // Mirror of `sessionLapsed` for the window listeners, which close over the
  // value at subscribe time and would otherwise read a stale `false`.
  const lapsedRef = useRef(false);
  const renewInFlightRef = useRef(false);
  const lastRenewAttemptRef = useRef(0);
  // Activity-driven renewal (below) needs to know whether the user has done
  // anything since the last time the session was renewed.
  //
  // Starts at 0, not `Date.now()`: seeding it with the mount time would make
  // *opening* a tab count as using it, and an inherited tab left untouched all
  // day would renew itself indefinitely — precisely the session the cap is for.
  const lastActivityRef = useRef(0);
  const lastRenewedAtRef = useRef(0);

  /**
   * The token is gone; the page is not.
   *
   * This is the whole point of the rewrite: a lapse leaves `user` in state, so
   * `ProtectedRoute` keeps rendering its children and the half-written CEL
   * expression, the open HITL review and the unsaved order correction all
   * survive. `ReauthDialog` blocks interaction on top until the session is
   * restored.
   *
   * Retaining `user` grants nothing: the token is dead, every request 401s, and
   * the backend is and remains the gate. What is retained is DOM the user was
   * already looking at.
   */
  /**
   * Open the re-auth prompt deliberately, before anything has lapsed.
   *
   * This is what makes the warning banner useful to password sessions today,
   * with no backend: signing in again while the current token is *still valid*
   * mints a full-length replacement, so "Sitzung verlängern" really does extend
   * the session rather than merely postponing the argument.
   */
  const requestReauth = useCallback((): void => setReauthRequested(true), []);
  const cancelReauth = useCallback((): void => {
    setReauthRequested(false);
    setRenewalRefusal(null);
  }, []);

  const beginLapse = useCallback((reason: string): void => {
    if (lapsedRef.current) return;
    console.warn('🔒 [AUTH CONTEXT] Session lapsed — prompting in place:', reason);
    lapsedRef.current = true;
    setSessionLapsedFlag(true);
    setSessionLapsed(true);
  }, []);

  const endLapse = useCallback((): void => {
    lapsedRef.current = false;
    setSessionLapsedFlag(false);
    setSessionLapsed(false);
    setReauthRequested(false);
    setRenewalRefusal(null);
  }, []);

  const logout = useCallback((): void => {
    console.log('🚪 [AUTH CONTEXT] Logging out user:', user?.username);
    clearPortalAdminTenantOverride();
    // Tell the server first — it needs the token that is about to be deleted.
    // `POST /auth/logout` was a no-op for as long as the session was a bare
    // stateless JWT, so the UI never called it; it now records a revocation
    // against the token's `sid`, which is the only thing that stops a copied
    // token from outliving the sign-out.
    void AuthService.revokeSessionOnServer();
    AuthService.logout();
    AuthService.pruneExpiredUsers();
    endLapse();
    setSessionExpiresAt(null);
    setExpiryWarningDismissed(false);
    setUser(null);
  }, [user?.username, endLapse]);

  // Adopt whatever is in storage as this tab's live session. Used after a
  // renewal (this tab's or another's) puts a fresh record there.
  const adoptStoredSession = useCallback((): boolean => {
    const current = AuthService.getCurrentUser();
    if (!current) return false;
    setUser(current);
    endLapse();
    setSessionExpiresAt(null);
    setExpiryWarningDismissed(false);
    return true;
  }, [endLapse]);

  /**
   * Renew silently, if this session can be renewed at all.
   *
   * `AuthService.renewSession()` is the seam: it tries `POST /auth/refresh`
   * first for every provider (the only path that carries the *same* session
   * forward, so the absolute cap keeps counting) and falls back to the SSO
   * exchange, which mints a new session and resets the cap.
   */
  const attemptRenewal = useCallback(async (respectCooldown: boolean): Promise<boolean> => {
    if (renewInFlightRef.current) return false;
    if (respectCooldown && Date.now() - lastRenewAttemptRef.current < RENEW_COOLDOWN_MS) {
      return false;
    }

    renewInFlightRef.current = true;
    lastRenewAttemptRef.current = Date.now();
    try {
      const outcome = await AuthService.renewSession();

      if (outcome.status === 'renewed') {
        // `handleAccessToken` has already rewritten the stored record and fired
        // `auth-updated`; adopt it here too so the banner clears in this tick
        // rather than on the next one.
        lastRenewedAtRef.current = Date.now();
        return adoptStoredSession();
      }

      if (outcome.status === 'rejected') {
        // The backend has decided this session is over — the absolute cap, a
        // revocation, a deactivated account. No amount of retrying changes
        // that, so ask for a real sign-in now rather than at `exp`.
        setRenewalRefusal(outcome.reason);
        requestReauth();
      }

      // 'retry' (network, 5xx) and 'unavailable' both stay quiet: the first is
      // transient, the second is the ordinary state of a session this build
      // cannot renew, and the expiry warning already covers it.
      if (outcome.status === 'retry') {
        // Don't let a network blip consume the cooldown.
        lastRenewAttemptRef.current = 0;
      }
      return false;
    } finally {
      renewInFlightRef.current = false;
    }
  }, [adoptStoredSession, requestReauth]);

  /**
   * Watch the session clock.
   *
   * Three outcomes, in order of preference: renew it silently; warn with an
   * action; lapse in place. What it must never do is what it used to — end the
   * session and take the page with it.
   */
  const checkTokenExpiration = useCallback((): void => {
    if (AuthService.isTokenExpired()) {
      // A tab busy enough to be making requests usually reaches the 401 path
      // first; this covers the idle one that would otherwise go on claiming to
      // be signed in.
      beginLapse('token_expired');
      return;
    }

    if (!AuthService.isTokenExpiringSoon()) {
      setSessionExpiresAt(null);

      /**
       * Renew on activity, well before the deadline.
       *
       * This is the step that makes "the session ends because you stopped
       * working" literally true. Past the token's half-life, a user who is
       * still here gets a new token and never sees a banner; a tab someone
       * walked away from renews nothing and expires on schedule, which is the
       * security property worth keeping.
       *
       * Requires the activity to be *since the last renewal* — otherwise one
       * click in the morning would keep a session alive all day.
       */
      if (
        AuthService.isTokenPastHalfLife() &&
        lastActivityRef.current > lastRenewedAtRef.current
      ) {
        void attemptRenewal(true);
      }
      return;
    }

    // Inside the warning window, renew only for someone who is still here.
    //
    // Renewing unconditionally here is what used to defeat the idle timeout:
    // `/auth/refresh` succeeds for any valid token, so an abandoned tab slid
    // its own session forward every cooldown period all the way to the
    // absolute cap — and the banner never appeared, because it is only set
    // when renewal *fails*. An unlocked laptop stayed signed in for twelve
    // hours while the comment above claimed the opposite.
    //
    // The rule is the same one the half-life branch uses — activity since the
    // last renewal — so both paths now agree on what "still working" means.
    // A deliberate click on "extend" goes through `renewSessionNow`, which is
    // exempt from this and from the cooldown.
    if (lastActivityRef.current <= lastRenewedAtRef.current) {
      setSessionExpiresAt(AuthService.getSessionExpiry());
      return;
    }

    void attemptRenewal(true).then(renewed => {
      // Only warn about what renewal could not quietly handle. The banner now
      // means "we tried and could not", which is why it is allowed to carry an
      // action.
      if (!renewed) setSessionExpiresAt(AuthService.getSessionExpiry());
    });
  }, [attemptRenewal, beginLapse]);

  useEffect(() => {
    // Check for existing authentication on app start
    // If this tab doesn't have a user, automatically use an existing logged-in user
    // This allows new tabs to inherit an existing login
    const initAuth = () => {
      try {
        // The getters no longer evict expired records as a side effect of being
        // read, so sweep once at start-up — otherwise dead sessions linger in
        // localStorage and get offered to the next new tab.
        AuthService.pruneExpiredUsers();
        const currentUser = AuthService.initializeTabUser();
        setUser(currentUser);
        if (currentUser) {
          console.log('✅ [AUTH CONTEXT] Tab initialized with user:', currentUser.username);
        } else {
          console.log('ℹ️ [AUTH CONTEXT] No user available for this tab');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Keep AuthProvider state in sync when tokens are exchanged asynchronously
  // (e.g., `Google EasyAuth -> backend exchange` happening after LoginForm mount,
  // or a renewal landing in this tab).
  useEffect(() => {
    const onAuthUpdated = () => {
      try {
        const currentUser = AuthService.initializeTabUser();
        // A lapsed tab must not be signed out by this listener. The record is
        // gone or expired by definition during a lapse — and another tab's
        // start-up `pruneExpiredUsers()` can delete it outright — so adopting
        // that `null` would unmount the tree behind the dialog, which is the
        // exact failure the lapse exists to prevent.
        if (!currentUser && lapsedRef.current) return;
        if (currentUser) endLapse();
        setUser(currentUser);
      } catch (error) {
        console.error('Error refreshing auth after auth-updated event:', error);
        if (!lapsedRef.current) setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    window.addEventListener('auth-updated', onAuthUpdated);
    return () => {
      window.removeEventListener('auth-updated', onAuthUpdated);
    };
  }, [endLapse]);

  /**
   * Every other site that notices a dead token — the axios 401 interceptor, the
   * chat websocket's auth close — announces it here rather than ending the
   * session itself. One place decides what a dead token means.
   */
  useEffect(() => {
    const onSessionExpired = (event: Event) => {
      const reason = (event as CustomEvent).detail?.reason ?? 'unknown';
      // Nothing to lapse into: no session was ever established in this tab, so
      // `LoginForm` (and `App`'s toast) is the correct destination.
      if (!user) return;
      beginLapse(reason);
    };

    window.addEventListener('session-expired', onSessionExpired);
    return () => {
      window.removeEventListener('session-expired', onSessionExpired);
    };
  }, [user, beginLapse]);

  /**
   * Another tab renewed or re-authenticated.
   *
   * `auth-updated` is a same-tab `window` event and `AuthService.login()` does
   * not dispatch it at all, so without this a second tab would sit on a stale
   * banner — or a lapse dialog — for up to a minute after the session was
   * already restored next door.
   */
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key && !/^auth(Token|Expires|UsersList)/.test(event.key)) return;
      if (lapsedRef.current) {
        adoptStoredSession();
        return;
      }
      checkTokenExpiration();
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [adoptStoredSession, checkTokenExpiration]);

  // Cheap, passive, and never re-rendering: activity only has to be *recorded*,
  // and the 60s check is what reads it.
  useEffect(() => {
    const noteActivity = () => { lastActivityRef.current = Date.now(); };
    const windowEvents: Array<keyof WindowEventMap> = ['pointerdown', 'keydown'];
    windowEvents.forEach(name => window.addEventListener(name, noteActivity, { passive: true }));
    // `visibilitychange` is a document event, not a window one — coming back to
    // a tab counts as using it.
    document.addEventListener('visibilitychange', noteActivity, { passive: true });
    return () => {
      windowEvents.forEach(name => window.removeEventListener(name, noteActivity));
      document.removeEventListener('visibilitychange', noteActivity);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    console.log('⏰ [AUTH CONTEXT] Setting up token expiration check for user:', user.username);
    checkTokenExpiration();
    const interval = setInterval(checkTokenExpiration, 60000);

    return () => {
      console.log('🧹 [AUTH CONTEXT] Cleaning up token expiration check for user:', user.username);
      clearInterval(interval);
    };
  }, [user, checkTokenExpiration]);

  const login = async (username: string, password: string): Promise<void> => {
    try {
      console.log('🔐 [AUTH CONTEXT] Starting login process for user:', username);
      setIsLoading(true);
      const authUser = await AuthService.login(username, password);
      console.log('✅ [AUTH CONTEXT] Login successful for user:', username);
      endLapse();
      setSessionExpiresAt(null);
      setExpiryWarningDismissed(false);
      setUser(authUser);
    } catch (error: any) {
      console.error('❌ [AUTH CONTEXT] Login failed for user:', username, error);
      // Ensure we re-throw the error with proper message
      const errorMessage = error?.message || 'Login failed. Please try again.';
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Sign in again without unmounting the app.
   *
   * Why this exists instead of calling `login()`: `login()` sets `isLoading`,
   * and `isAuthenticated` is `user !== null && !isLoading`, so for the duration
   * of the round-trip `ProtectedRoute` would render `LoginForm` — destroying
   * exactly the unsaved work the re-auth dialog was built to protect. This path
   * never touches `isLoading`.
   *
   * It also refuses to sign in as somebody else. `/auth/login` will happily
   * accept another person's credentials, and accepting them here would leave
   * one user's half-finished rule on screen under another user's session.
   */
  const reauth = useCallback(async (password: string): Promise<void> => {
    const lapsedUser = user;
    if (!lapsedUser) throw new Error('No session to restore.');

    const restored = await AuthService.login(lapsedUser.username, password);

    if (lapsedUser.id && restored.id && restored.id !== lapsedUser.id) {
      console.warn('⚠️ [AUTH CONTEXT] Re-auth returned a different identity — signing out');
      AuthService.logout(restored.id);
      AuthService.logout(lapsedUser.id);
      endLapse();
      setSessionExpiresAt(null);
      setExpiryWarningDismissed(false);
      setUser(null);
      throw new Error('DIFFERENT_USER');
    }

    endLapse();
    setSessionExpiresAt(null);
    setExpiryWarningDismissed(false);
    lastRenewAttemptRef.current = 0;
    setUser(restored);

    // `login()` does not fire this; other tabs pick the new record up through
    // `storage`, but same-tab consumers (the chat socket, which paused rather
    // than reloading) need the nudge to reconnect.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('auth-updated'));
    }
  }, [user, endLapse]);

  const renewSessionNow = useCallback(async (): Promise<boolean> => {
    return attemptRenewal(false);
  }, [attemptRenewal]);

  // A lapsed session is still "authenticated" in the only sense this flag
  // decides — may this tree stay mounted. Whether new work may *start* is
  // `sessionLapsed`, which the chat socket and the pollers check instead.
  const isAuthenticated = user !== null && !isLoading;

  const dismissExpiryWarning = useCallback(() => {
    // Sticky until the next sign-in: the 60s check would otherwise reopen the
    // banner every minute for the rest of the session.
    setExpiryWarningDismissed(true);
  }, []);

  const contextValue: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated,
    isLoading,
    sessionExpiresAt,
    dismissExpiryWarning,
    expiryWarningDismissed,
    sessionLapsed,
    reauth,
    renewSessionNow,
    reauthRequested,
    requestReauth,
    cancelReauth,
    renewalRefusal,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
